import { AgGridAngular, AgGridModule } from '@ag-grid-community/angular';
import {
	ColDef,
	ColumnState,
	GridApi,
	GridOptions,
	GridReadyEvent,
	ICellRendererParams,
	RowEvent
} from '@ag-grid-community/core';
import { Location, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	HostBinding,
	Injector,
	OnDestroy,
	ViewChild,
	ViewEncapsulation
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { LuceneExpression, toString as luceneToString } from 'lucene';
import moment from 'moment';
import { fromEvent, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';

import { QualityIssue } from '../../claim/models/quality-issue';
import { Document, DocumentExport, DocumentLine } from '../../commercial-documents/models/document';
import { API_SALESDOC_SERVER_URL } from '../../config';
import { State } from '../../order/models/localization-state';
import filterIcon from '../svg/filters.svg';
import { safe } from '../utils/safe';
import { ScheduleBusinessOrder } from '../../order/models/schedule-business-order';
import { Shipment } from '../../order/models/shipment';
import { Organization } from '../../organization/models/organization';
import { pageTransitions } from '../animations/page-transitions';
import { FilterModel } from '../models/ag-grid';
import { DatacenterType, UserDatacenterView } from '../models/datacenter-aggrid-view';
import { User } from '../models/user';
import { ConnectionService } from '../services/connection.service';
import { DatacenterFilterUtilsService } from '../services/datacenter-filter-utils.service';
import { DatacenterSelectionService } from '../services/datacenter-selection.service';
import { DatacenterViewService } from '../services/datacenter-view.service';
import { CustomLocation } from '../services/location.custom';
import { TranslationService } from '../services/translation.service';
import { UserService } from '../services/user.service';
import { WorkerService } from '../services/worker.service';
import { OrderBy } from '../utils/constant';
import { truncateTwoDecimals } from '../utils/number.format';
import { resolveSingleConcatenatedData, resolveStringCellValue } from '../utils/resolved-concatenated-value';
import { Batch } from '../../order/models/batch';
import { UsersDatacenter } from '../../users/models/user';
import { DatacenterFilterType } from '../models/datacenter-filters';
import { DatacenterLuceneFilterService } from '../services/datacenter-lucene-filter.service';
import { DbAnalyticsService } from '../services/db-analytics.service';

import { ColDefWithFields } from './column-type';
import { DataSource } from './data-source';
import { DatacenterDefaultConfiguration } from './datacenter-default-configuration';
import { DatacenterToolHeaderComponent } from './datacenter-tool-header.component';
import { clientSideAgGridModules, CommonGridOptions, serverSideAgGridModules } from './grid-options';
import { CheckboxHeaderComponent } from './header-components/checkbox-header.component';
import { ContextDatacenter } from './types/context-datacenter';
import {
	DetailedReportData,
	DetailedReportDialogComponent
} from './types/schedule-business-orders/components/detailed-report-dialog.component';
import { Strategy } from './types/strategy';

const resizeObservable = fromEvent(globalThis, 'resize');

const GET_DOCUMENT_URL = `${ API_SALESDOC_SERVER_URL }/v1/documents`;

const LIMIT = 250;

function isExportable(columnDef: ColDefWithFields):
	columnDef is ColDefWithFields & { headerName: NonNullable<ColDefWithFields['headerName']> } {
	return columnDef.columnType !== undefined
		&& columnDef.headerName !== undefined;
}

@Component({
	selector: 'copo-datacenter',
	templateUrl: './datacenter.component.html',
	animations: pageTransitions,
	providers: [DatacenterSelectionService],
	encapsulation: ViewEncapsulation.Emulated,
	styleUrl: './datacenter.component.css',

	imports: [TranslateModule, NgIf, MatButtonModule, MatIconModule, DatacenterToolHeaderComponent, AgGridModule]
})
export class DatacenterComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	@ViewChild('agGrid') agGrid!: AgGridAngular;
	datacenterType: DatacenterType;
	datacenterClass?: Strategy;
	contextDatacenter: ContextDatacenter;
	gridOptions: GridOptions;
	datacenterTitle: string;
	exportExcelName: string;
	resizeSub?: Subscription;
	orderBySub?: Subscription;
	filterSub?: Subscription;
	datacenterTypeSub?: Subscription;
	count: number | undefined;
	filter: string | undefined;
	orderBy: OrderBy<string>[] | undefined;
	user?: User;
	state: State;
	exportInProgress = false;
	allSub?: Subscription;
	rowCountsSub?: Subscription;
	dataSource?: DataSource;
	currentColumns: string[] = [];
	currentSelectedView: UserDatacenterView;
	numberOfFiltersApplied = 0;
	isDocumentDatacenter = false;
	isAqdbDataCenter = false;
	buttonName = '';
	displayTotal = false;
	isFilterContainsAggregateField = false;
	aggregateSubscription?: Subscription;
	dataGridReady = new EventEmitter<GridApi>();
	readonly userViews: UserDatacenterView[] = [];
	readonly agGridModules = serverSideAgGridModules;
	readonly agHeaderModules = clientSideAgGridModules;
	private readonly stateId: number;
	private gridApi?: GridApi<unknown>;
	private readonly qpSub?: Subscription;
	private pendingFields?: string;
	private readonly filterState: FilterModel | undefined;
	private readonly sortState: ColumnState[] | undefined;
	private readonly gridState: { gridIndex: number } | undefined;
	private viewConfigReported = false;

	constructor(
		readonly translationService: TranslationService,
		readonly userService: UserService,
		readonly location: Location,
		readonly injector: Injector,
		readonly workerService: WorkerService,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly router: Router,
		private readonly route: ActivatedRoute,
		private readonly viewService: DatacenterViewService,
		readonly selectionService: DatacenterSelectionService,
		private readonly filterService: DatacenterFilterUtilsService,
		private readonly connectionService: ConnectionService,
		readonly datacenterLuceneFilterService: DatacenterLuceneFilterService,

		private readonly snackBar: MatSnackBar,

		private readonly dialog: MatDialog,
		private readonly dbAnalyticsService: DbAnalyticsService
	) {
		this.stateId = CustomLocation.GET_CURRENT_STATE_ID();
		this.filterState = <FilterModel | undefined>CustomLocation.GET_STATE('filterState', this.stateId);
		this.sortState = <ColumnState[] | undefined>CustomLocation.GET_STATE('sortState', this.stateId);
		this.gridState = <{ gridIndex: number } | undefined>CustomLocation.GET_STATE('gridState', this.stateId);
		this.datacenterType = <DatacenterType> this.route.snapshot.data['type'];
		this.contextDatacenter = new ContextDatacenter(this.datacenterType, this.injector);
		this.datacenterTitle = <string> this.route.snapshot.data['title'];
		this.exportExcelName = <string> this.route.snapshot.data['exportExcelName'];
		this.user = this.userService.user;
		this.state = <State> location.getState();
		this.datacenterTypeSub = this.route.data.subscribe(res => {
			this.datacenterType = <DatacenterType> res['type'];
			this.datacenterTitle = <string> res['title'];
			this.exportExcelName = <string> res['exportExcelName'];
		});
		const defaultView = defaultConfiguration.getDefaultView(this.datacenterType);
		defaultView.name = translationService.getTR('datacenter.default');
		this.userViews.push(defaultView);
		const translatableViews = new Set(['DispatchAdvice', 'Certificate', 'Invoice', 'OrderAcknowledgment']);
		for (const view of viewService.getViewList(this.datacenterType)) {
			if (translatableViews.has(view.name)) {
				const translationKey = `datacenterview.${ view.name }`;
				const translatedName = this.translationService.getTR(translationKey);
				const updatedView: UserDatacenterView = {
					...view,
					name: translatedName
				};

				this.userViews.push(updatedView);
			} else {
				this.userViews.push(view);
			}
		}
		const queryParams = this.route.snapshot.queryParamMap;
		const hasUrlOverrides = queryParams.has('fields')
			|| queryParams.has('orderBy')
			|| queryParams.has('filter')
			|| queryParams.has('view');
		if (hasUrlOverrides) {
			const fields = queryParams.get('fields');
			const orderBy = queryParams.get('orderBy');
			const filterRaw = queryParams.get('filter');
			const viewParam = queryParams.get('view');
			const view = this.resolveViewFromUrlParam(viewParam, defaultView);

			const columnState = fields
				? fields.split(',').map(colId => ({ colId }))
				: view.columnState;

			let { sortModel } = view;
			if (orderBy) {
				const [colId, sort] = orderBy.split(' ');
				sortModel = [{ colId, sort: sort === 'asc' || sort === 'desc' ? sort : 'asc' }];
			}

			let filterState: DatacenterFilterType[] | null | undefined = view.filterState;
			if (filterRaw) {
				const luceneQuery = decodeURIComponent(filterRaw);

				const luceneFilters = this.datacenterLuceneFilterService.convertToFilterTypes(
					luceneQuery,
					this.datacenterType
				);

				filterState = view.filterState
					? [...view.filterState, ...luceneFilters]
					: [...luceneFilters];
			}
			const urlView: UserDatacenterView = {
				...view,
				columnState,
				sortModel,
				filterState
			};

			this.currentSelectedView = urlView;
			this.viewService.viewToEdit = { currentView: urlView, viewType: this.datacenterType };
			history.replaceState({ view: urlView }, '');
			this.qpSub = this.route.queryParamMap.subscribe(pm => {
				const field = pm.get('fields');
				if (!field) {
					return;
				}
				if (!this.gridApi || !this.dataSource) {
					this.pendingFields = field;
					return;
				}
				this.applyFieldsToGrid(field);
			});
		} else {
			this.currentSelectedView = this.getCurrentView();
		}

		this.displayTotal = this.contextDatacenter.hasTotal();
		this.gridOptions = this.getGridOptions();
	}

	notAllNodeSelected() {
		return this.selectionService.selectedNodes.length !== 0 && !this.selectionService.isAllSelected;
	}

	async exportExcelData() {
		this.dbAnalyticsService.trackAction('EXPORT_EXCEL', {
			fields: this.currentColumns,
			filters: this.filter ?? null,
			sortBy: this.orderBy?.map(o => `${ o.field } ${ o.direction }`) ?? [],
		});
		this.exportInProgress = true;
		if (this.allSub) {
			this.allSub.unsubscribe();
		}
		return this.getDataToExport().then(async (dataToExport: unknown[]) => {
			const columnDefs = this.agGrid.api.getAllDisplayedColumns()
				.map(column => <ColDefWithFields> column.getColDef())
				.filter(isExportable);
			const formatedData = this.formatExportedData(dataToExport, columnDefs);
			const columnTypes = columnDefs.map(columnDef => columnDef.columnType);
			await this.exportToExcel(formatedData, columnTypes);
		}).catch((err: unknown) => {
			console.warn('unable export to excel', err);
		});
	}

	exportAsZipData() {
		this.dbAnalyticsService.trackAction('DOWNLOAD_PDF', {
			documentType: this.datacenterType,
			documentCount: this.selectionService.isAllSelected
				? this.count ?? this.selectionService.selectedCount
				: this.selectionService.selectedCount,
		});
		this.getDataToExport().then((dataToExports: unknown[]) => {
			const exportDatas = <Document[]> dataToExports;
			this.getDownloadZipURL(exportDatas);
		}).catch((err: unknown) => {
			console.warn('export data', err);
		});
	}

	async exportAqdbBatchData() {
		this.exportInProgress = true;
		if (this.allSub) {
			this.allSub.unsubscribe();
		}
		this.currentColumns = [];
		return this.getAQDBDataToExport().then(async (dataToExport: unknown[]) => {
			const columnDefs = this.agGrid.api.getAllGridColumns()
				.map(column => <ColDefWithFields>column.getColDef())
				.filter(isExportable);
			const formatedData = this.formatExportedData(dataToExport, columnDefs);
			const columnTypes = columnDefs.map(columnDef => columnDef.type);
			await this.exportToExcel(formatedData, columnTypes);
		}).catch((err: unknown) => {
			console.warn('unable export to excel', err);
		});
	}

	filterCondition(fieldName: string, values: string[]) {
		let filterCondition: LuceneExpression;
		if (values.length === 1) {
			filterCondition = {
				field: fieldName,
				term: values[0],
				quoted: true
			};
		} else {
			filterCondition = {
				field: fieldName,
				left: {
					field: '<implicit>',
					term: values[0],
					quoted: true
				},
				operator: '<implicit>',
				right: {
					field: '<implicit>',
					term: values[1],
					quoted: true
				},
				parenthesized: true
			};
			let current = filterCondition;
			for (const aqdbBatchId of values.slice(2)) {
				current.right = {
					left: current.right,
					operator: '<implicit>',
					right: {
						field: '<implicit>',
						term: aqdbBatchId,
						quoted: true
					},
					parenthesized: false
				};
				current = current.right;
			}
		}
		return filterCondition;
	}

	async getAQDBDataToExport() {
		let downloadFilter = '';
		if (this.notAllNodeSelected()) {
			const ids: string[] = this.selectionService.selectedNodes
				.map(rowNode => (<Document>rowNode.data).id.toString());
			downloadFilter = luceneToString(this.filterCondition('id', ids));
		}
		return this.contextDatacenter.getAllDataSelected(
			this.currentColumns,
			downloadFilter || this.filter,
			this.orderBy
		);
	}

	dataCenterType() {
		if (this.datacenterType === 'document') {
			this.isDocumentDatacenter = true;
			this.buttonName = this.translationService.getTR('download.pdf-files');
			return true;
		} else if (this.datacenterType === 'schedule-business-order') {
			this.buttonName = this.translationService.getTR('download-detailed-report');
			return true;
		}
		if (this.datacenterType === 'aqdb') {
			this.isAqdbDataCenter = true;
			return true;
		}
		return false;
	}

	detailedReport() {
		if (this.isSelectionTooBig(1000)) {
			this.snackBar.open(this.translationService.getTR('warning.maximum.1000'), undefined, {
				duration: 2000,
			});
		} else {
			this.getIdsToExport().then((dataToExports: unknown[]) => {
				const supplierOrderNumbers: string[] = [];
				const exportDatas = <ScheduleBusinessOrder[]> dataToExports;
				for (const exportData of exportDatas) {
					supplierOrderNumbers.push(exportData.supplierOrderNumber);
				}
				this.dialog.open<DetailedReportDialogComponent, DetailedReportData, string | undefined>(
					DetailedReportDialogComponent,
					{
						panelClass: 'save-view-block',
						data: {
							id: supplierOrderNumbers
						},
						disableClose: true,
					}
				);
			}).catch((err: unknown) => {
				console.warn('export data', err);
			});
		}
	}

	collateDocuments() {
		this.dbAnalyticsService.trackAction('COLLATE_DOCUMENTS', {
			documentType: this.datacenterType,
			documentCount: this.selectionService.selectedNodes.length,
		});
		this.getDataToExport().then((dataToExports: unknown[]) => {
			const exportDatas = <Document[]> dataToExports;
			this.getCollateDocumentURL(exportDatas);
		}).catch((err: unknown) => {
			console.warn('export data', err);
		});
	}

	getDownloadZipUrl(ids: string[], filename: string) {
		const url = new URL(`${ GET_DOCUMENT_URL }/download/${ filename }.zip`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		params.append('download', 'true');
		for (const identifier of ids) {
			params.append('documents', identifier);
		}
		return url.toString();
	}

	getCollateDocumentURL(exportDatas: Document[]) {
		const documentIds: string[] = [];
		for (const exportData of exportDatas) {
			documentIds.push(exportData.id.toString());
		}
		const url = new URL(`${ GET_DOCUMENT_URL }/download-combined-pdf/CollatedDocuments.pdf`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		params.append('download', 'true');
		for (const identifier of documentIds) {
			params.append('documents', identifier);
		}
		open(url.toString());
	}

	async getDataToExport() {
		if (this.notAllNodeSelected()) {
			return this.selectionService.selectedNodes
				.map(rowNode => rowNode.data);
		}
		return this.getAllData();
	}

	async getIdsToExport() {
		if (this.notAllNodeSelected()) {
			return this.selectionService.selectedNodes
				.map(rowNode => (<ScheduleBusinessOrder> rowNode.data));
		}
		return this.getAllData();
	}

	isSelectionTooBig(value: number) {
		return this.selectionService.selectedCount > value;
	}

	async getAllData() {
		return this.contextDatacenter.getAllDataSelected(this.currentColumns, this.filter, this.orderBy);
	}

	formatExportedData(dataToExport: unknown[], columnDefs: (ColDefWithFields & { headerName: string })[]) {
		if (this.datacenterType === 'shipment') {
			if (this.user?.getSettings('download')?.value.type === 'duplicate') {
				const exportData = this.buildShipmentDuplicateData(<Shipment[]> dataToExport);
				return [
					columnDefs.map(columnDef => columnDef.headerName),
					...exportData.map(
						data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
					)
				];
			}
			for (const data of <Shipment[]>dataToExport) {
				if (data.qtyBatchBrutConcat) {
					data.qtyBatchBrutConcat = this.processConcat(data.qtyBatchBrutConcat, val =>
						Number.parseFloat(val).toString());
				}
				if (data.thicknessDimensionConcat) {
					data.thicknessDimensionConcat = this.processConcat(data.thicknessDimensionConcat, val =>
						Number.parseFloat(val).toString());
				}
				if (data.widthDimensionConcat) {
					data.widthDimensionConcat = this.processConcat(data.widthDimensionConcat, val =>
						Number.parseFloat(val).toString());
				}
				if (data.transportReferenceConcat) {
					data.transportReferenceConcat = data.transportReferenceConcat.includes(';')
						? data.transportReferenceConcat.split(';')
							.filter((value, index, self) => self.indexOf(value) === index).join(';')
						: data.transportReferenceConcat;
				}
				if (data.customerOrderAndItemConcat) {
					data.customerOrderAndItemConcat = data.customerOrderAndItemConcat.includes(';')
						? resolveStringCellValue(data.customerOrderAndItemConcat).join(';')
						: resolveSingleConcatenatedData(data.customerOrderAndItemConcat);
				}
				if (data.supplierOrderAndItemConcat) {
					data.supplierOrderAndItemConcat = data.supplierOrderAndItemConcat.includes(';')
						? resolveStringCellValue(data.supplierOrderAndItemConcat).join(';')
						: resolveSingleConcatenatedData(data.supplierOrderAndItemConcat);
				}
				if (data.supplierOrderTypeConcat) {
					data.supplierOrderTypeConcat = this.getSupplierOrderTypeValue(data.supplierOrderTypeConcat);
				}
			}
		}
		if (this.datacenterType === 'organization') {
			const exportData = <Organization[]> dataToExport;
			for (const data of exportData) {
				data.suppliersString = data.suppliers ? data.suppliers.map(item => item.name).join(';') : '';
				data.city = data.address.city;
				data.role = data.roleDescription;
				data.countryCodeDescription = data.address.countryCodeDescription;
				data.status = data.statusDescription;
			}

			return [
				columnDefs.map(columnDef => columnDef.headerName),
				...exportData.map(
					data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
				)
			];
		}
		if (this.datacenterType === 'users') {
			const exportData = <UsersDatacenter[]>dataToExport;
			for (const data of exportData) {
				data.role = data.role ? this.translationService.getTR(
					`organizationrole.${ data.role.trim().toLowerCase() }`
				) : '';
				data.status = data.status.trim() === 'pend'
					? this.translationService.getTR('issue.pending')
					: data.personStatus.trim() === 'pend' ? this.translationService.getTR('wait.user.validation')
						: this.translationService.getTR('active');
			}
			return [
				columnDefs.map(columnDef => columnDef.headerName),
				...exportData.map(
					data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
				)
			];
		}
		if (this.datacenterType === 'quality') {
			const exportData = <QualityIssue[]> dataToExport;
			for (const data of exportData) {
				data.mill = data.order ? data.order.millDescription : '';
				data.millDescription = data.order ? data.order.millDescription : '';
			}
		}
		if (this.datacenterType === 'document') {
			const exportData = <Document[]> dataToExport;
			for (const data of exportData) {
				if (data.proforma === 'true') {
					data.proforma = this.translationService.getTR('global.yes');
				} else if (data.proforma === 'false') {
					data.proforma = this.translationService.getTR('global.no');
				}
			}
		}
		if (this.datacenterType === 'document' && this.user?.getSettings('download')?.value.type === 'duplicate') {
			const documentsToExport: DocumentExport[] = [];
			for (const data of <Document[]>dataToExport) {
				const inputDataType = new Set(['CE', 'IN', 'DA', 'XCARB']);
				let documentToExport: DocumentExport | null = this.createDocumentToExport(data);
				for (const documentLine of data.documentLines) {
					if ((data.type === 'OA' && documentLine.supplierOrderReferenceItem)
						|| (inputDataType.has(data.type) && documentLine.batchNumber)) {
						if (documentToExport === null) {
							documentToExport = this.createDocumentToExport(data);
						}
						this.setDocumentLineToExport(documentToExport, documentLine);
						documentsToExport.push(documentToExport);
						documentToExport = null;
					}
				}
				if (documentToExport !== null) {
					documentsToExport.push(documentToExport);
				}
			}
			return [
				columnDefs.map(columnDef => columnDef.headerName),
				...documentsToExport.map(
					document =>
						columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, document))
				)
			];
		}
		if (this.datacenterType === 'batch' || this.datacenterType === 'calloff-by-batch') {
			const exportData = <Batch[]>dataToExport;
			for (const data of exportData) {
				data.producedLength = data.form === '04' && data.producedLength
					? truncateTwoDecimals(data.producedLength / 1000) : undefined;
				if (data.supplierOrderType) {
					data.supplierOrderType = this.getSupplierOrderTypeValue(data.supplierOrderType);
				}
			}
		}
		return [
			columnDefs.map(columnDef => columnDef.headerName),
			...dataToExport.map(
				data => columnDefs.map(columnDef => this.contextDatacenter.formatCellByType(columnDef, data))
			)
		];
	}

	processConcat(value: string, transform: (val: string) => string): string {
		return value.includes(';') ? value.split(';').map(transform).join(';') : transform(value);
	}

	setDocumentLineToExport(documentToExport: DocumentExport, documentLine: DocumentLine) {
		documentToExport.millCode = documentLine.millCode;
		documentToExport.enduserBusinessId = documentLine.enduserBusinessId;
		documentToExport.supplierGroupId = documentLine.supplierGroupId;
		documentToExport.customerOrderReference ??= documentLine.customerOrderReference;
		documentToExport.customerOrderItemReference = documentLine.customerOrderItemReference;
		documentToExport.supplierOrderReference ??= documentLine.supplierOrderReference;
		documentToExport.supplierOrderReferenceItem = documentLine.supplierOrderReferenceItem;
		documentToExport.millOrderReference = documentLine.millOrderReference;
		documentToExport.millOrderReferenceItem = documentLine.millOrderReferenceItem;
		documentToExport.customerArticleReference = documentLine.customerArticleReference;
		documentToExport.supplierArticleReference = documentLine.supplierArticleReference;
		documentToExport.dispatchAdviceNumber = documentLine.dispatchAdviceNumber;
		documentToExport.batchNumber = documentLine.batchNumber;
		documentToExport.vendorBatchId = documentLine.vendorBatchId;
		documentToExport.heatNumber = documentLine.heatNumber;
		documentToExport.transportReference = documentLine.transportReference;
		documentToExport.packageSerialNumber = documentLine.packageSerialNumber;
		documentToExport.productDescription = documentLine.productDescription;
		documentToExport.length = documentLine.length;
		documentToExport.plantInvoiceNumber = documentLine.plantInvoiceNumber;
		documentToExport.invoiceVatPercentage = documentLine.invoiceVatPercentage;
		documentToExport.orderedQuantity = documentLine.orderedQuantity;
		documentToExport.steelGrade = documentLine.steelGrade;
		documentToExport.thickness = documentLine.thickness;
		documentToExport.width = documentLine.width;
		documentToExport.finalUse = documentLine.finalUse;
		documentToExport.oaLineStatus = documentLine.oaLineStatus;
		documentToExport.rejectionReason = documentLine.rejectionReason;
		documentToExport.displayOrderLines = documentLine.displayOrderLines;
		documentToExport.enduserName = documentLine.enduserName;
		documentToExport.receiptDocumentLineDate = documentLine.receiptDocumentLineDate;
		documentToExport.documentLineCreationBy = documentLine.documentLineCreationBy;
		documentToExport.documentLineModificationDate = documentLine.documentLineModificationDate;
		documentToExport.documentLineModificationBy = documentLine.documentLineModificationBy;
		documentToExport.invoiceCurrencyDescription = documentLine.invoiceCurrencyDescription;
		documentToExport.oaLineStatusDescription = documentLine.oaLineStatusDescription;
		documentToExport.isSelected = documentLine.isSelected;
		documentToExport.endQuarter = documentLine.endQuarter;
		documentToExport.documentLines?.push(documentLine);
	}

	createDocumentToExport(data: Document): DocumentExport {
		return {
			id: data.id,
			path: data.path,
			name: data.name,
			fileNamePdf: data.fileNamePdf,
			fileNameXml: data.fileNameXml,
			type: data.type,
			subtype: data.subtype,
			date: data.date,
			transportMode: data.transportMode,
			transmitterSystem: data.transmitterSystem,
			supplierVatNumber: data.supplierVatNumber,
			payerVatNumber: data.payerVatNumber,
			customerClaimReference: data.customerClaimReference,
			supplierClaimReference: data.supplierClaimReference,
			salesOrganization: data.salesOrganization,
			customerAnnouncementSegment: data.customerAnnouncementSegment,
			proforma: data.proforma,
			ceMarking: data.ceMarking,
			counterSigned: data.counterSigned,
			documentKey: data.documentKey,
			documentBase: data.documentBase,
			orderAcknowledgementDate: data.orderAcknowledgementDate,
			dispatchAdviceDate: data.dispatchAdviceDate,
			paymentDueDate: data.paymentDueDate,
			invoiceNumber: data.invoiceNumber,
			invoicedQuantity: data.invoicedQuantity,
			invoiceAmount: data.invoiceAmount,
			invoiceAmountWithoutVat: data.invoiceAmountWithoutVat,
			invoiceCurrency: data.invoiceCurrency,
			invoiceTaxAmount: data.invoiceTaxAmount,
			payerName: data.payerName,
			soldtoName: data.soldtoName,
			shiptoName: data.shiptoName,
			payerBusinessId: data.payerBusinessId,
			soldtoBusinessId: data.soldtoBusinessId,
			shiptoBusinessId: data.shiptoBusinessId,
			receiptDocumentDate: data.receiptDocumentDate,
			documentCreationBy: data.documentCreationBy,
			documentModificationDate: data.documentModificationDate,
			documentModificationBy: data.documentModificationBy,
			typeDescription: data.typeDescription,
			subtypeDescription: data.subtypeDescription,
			millDescription: data.millDescription,
			supplierMemberDescription: data.supplierMemberDescription,
			transportModeDescription: data.transportModeDescription,
			customerOrderReference: data.customerOrderReference,
			supplierOrderReference: data.supplierOrderReference,
			rrpType: data.rrpType,
			rrpDocumentLink: data.rrpDocumentLink,
			amount: data.amount,
			documentLines: []
		};
	}

	async exportToExcel(formatedDataToExport: string[][], columnTypes: (string | string[] | undefined)[]) {
		return new Promise<void>((resolve, reject) => {
			// if you change this line make sure that you change similar line inside gulp file
			const worker = this.workerService.createWorkerExcel();
			const fileName = `${ this.exportExcelName }-${ moment().format('YYYY MM DD') }.xlsx`;
			worker.addEventListener('message', ({ data }: { data: string }) => {
				this.exportInProgress = false;
				const link = document.createElement('a');
				link.download = fileName;
				link.href = data;
				link.append(document.createTextNode(fileName));
				document.body.append(link);
				link.click();
				link.remove();
				resolve();
				// let 10 seconds to the browser for downloading before revoking the url
				setTimeout(() => {
					URL.revokeObjectURL(data);
					worker.terminate();
				}, 10_000);
			});
			worker.addEventListener('error', error => {
				reject(new Error('Error in worker', { cause: error }));
				worker.terminate();
			});
			const sheets = [
				{
					fileName,
					rows: formatedDataToExport,
					workSheetName: this.exportExcelName,
					columnTypes,
					excludedSumColumns: ['Length (mm)', 'Thickness (mm)', 'Width (mm)']
				}
			];
			worker.postMessage({
				sheets
			});
		});
	}

	findDeselectedNode(id: string) {
		return this.selectionService.deselectedNodes.find(deselectedItem => deselectedItem.id === id);
	}

	onGridReady(params: GridReadyEvent) {
		this.gridApi = params.api;
		this.viewConfigReported = false;
		const view = this.getCurrentView();

		this.dataSource = new DataSource(
			this.contextDatacenter,
			this.viewService,
			this.filterService,
			this.currentColumns,
			view,
		);

		this.updateCurrentColumns(view);

		this.rowCountsSub = this.dataSource.subscribeToRowsCount({
			next: value => {
				this.count = value;
				this.selectionService.refreshCounterAfterFilter(this.count);
				this.updateSelected();
				if (!this.viewConfigReported && value !== undefined) {
					this.viewConfigReported = true;
					this.dbAnalyticsService.trackAction('DATACENTER_VIEW', {
						fields: this.currentColumns,
						filters: this.filter ?? null,
						sortBy: this.orderBy?.map(o => `${ o.field } ${ o.direction }`) ?? [],
					}, value);
				}
			}
		});

		this.filterSub = this.dataSource.subscribeToRowsFilter({
			next: value => {
				this.filter = value;
			}
		});

		this.orderBySub = this.dataSource.subscribeToRowsOrderBy({
			next: value => {
				this.orderBy = value;
			}
		});

		params.api.setGridOption('serverSideDatasource', this.dataSource);

		params.api.sizeColumnsToFit();
		this.resizeSub = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});

		if (this.displayTotal) {
			const aggregateFilter = this.contextDatacenter.createAggregateCustomViewFilter(view);
			const aggregateFields = this.contextDatacenter.getAggregateFields();
			this.isFilterContainsAggregateField = aggregateFields.some(value => aggregateFilter.includes(value));
			if (!this.isFilterContainsAggregateField) {
				this.gridApi.setGridOption('pinnedTopRowData', [{}]);
				this.aggregateSubscription = this.contextDatacenter.setTotal(this.gridApi, aggregateFilter);
			}
		}

		const { filterState, sortState, gridState } = this;
		if (filterState) {
			params.api.setFilterModel(filterState);
		}

		if (sortState) {
			params.api.applyColumnState({ state: sortState, applyOrder: true });
		}

		this.gridApi.setGridOption('onFirstDataRendered', frParams => {
			if (gridState) {
				if (!frParams.api.isLastRowIndexKnown()) {
					frParams.api.setRowCount((Math.floor((gridState.gridIndex + 1) / 250) + 2) * 250);
				}
				const rowCount = frParams.api.getDisplayedRowCount();
				if (gridState.gridIndex >= 0 && gridState.gridIndex < rowCount) {
					frParams.api.ensureIndexVisible(gridState.gridIndex, 'top');
				} else {
					console.warn(`Invalid row index for ensureIndexVisible: ${ rowCount }`);
				}
			}
		});
		this.dataGridReady.emit(this.gridApi);
		if (this.pendingFields) {
			this.applyFieldsToGrid(this.pendingFields);
			this.pendingFields = undefined;
		}
	}

	updateCurrentColumns(currentView: UserDatacenterView) {
		const currentColumns = [
			...new Set(currentView.columnState
				.flatMap(column => this.defaultConfiguration.getFieldsForColumn(currentView.type, column.colId)))
		];
		const requiredFields = this.defaultConfiguration.getRequiredFields(currentView.type);
		for (const field of requiredFields) {
			if (!currentColumns.includes(field)) {
				currentColumns.push(field);
			}
		}
		this.currentColumns = currentColumns;
		if (this.dataSource) {
			this.dataSource.currentColumns = currentColumns;
		}
	}

	updateSelected() {
		if (this.gridApi && this.selectionService.isAllSelected) {
			this.gridApi.forEachNode(node => {
				if (node.id && !this.findDeselectedNode(node.id)) {
					node.setSelected(true, false);
				}
			});
		}
	}

	onRowSelected(event: RowEvent<'rowSelected'>) {
		this.selectionService.onRowSelected(event, this.count);
	}

	ngOnDestroy() {
		if (this.resizeSub !== undefined) {
			this.resizeSub.unsubscribe();
		}
		if (this.allSub) {
			this.allSub.unsubscribe();
		}
		if (this.orderBySub) {
			this.orderBySub.unsubscribe();
		}
		if (this.filterSub) {
			this.filterSub.unsubscribe();
		}
		if (this.rowCountsSub) {
			this.rowCountsSub.unsubscribe();
		}
		if (this.datacenterTypeSub) {
			this.datacenterTypeSub.unsubscribe();
		}

		if (this.aggregateSubscription) {
			this.aggregateSubscription.unsubscribe();
		}

		this.qpSub?.unsubscribe();
	}

	async goToCustomizeView() {
		this.viewService.viewToEdit = {
			currentView: this.currentSelectedView,
			viewType: this.currentSelectedView.type
		};
		await this.router.navigate(
			['view'],
			{ queryParams: { type: this.currentSelectedView.type }, relativeTo: this.route }
		);
	}

	async setCurrentViewAndColumnState(view: UserDatacenterView) {
		if (view.filterState) {
			const aggregateFields = this.contextDatacenter.getAggregateFields();
			for (const filterState of view.filterState) {
				for (const value of filterState.values) {
					if (typeof value === 'string' || !Array.isArray(value)) {
						continue;
					}
					for (const item of value) {
						const match = aggregateFields.includes(item.apiName);
						if (match && this.gridApi) {
							this.gridApi.setGridOption('pinnedTopRowData', []);
						}
					}
				}
			}
		}

		await this.viewService.saveCurrentView(view);
		await this.router.navigate(
			[],
			{
				queryParams: { view: this.normalizeViewName(view.name) },
				relativeTo: this.route,
				replaceUrl: true
			}
		);
		this.currentSelectedView = view;
		this.updateCurrentColumns(view);

		if (this.gridApi && this.dataSource) {
			this.dataSource.currentView = view;
			this.gridApi.refreshServerSide({ purge: true });
			const columnDefs = this.createColumnDefs(view);
			this.gridApi.setGridOption('columnDefs', <ColDef<unknown>[]> columnDefs);
			this.gridApi.applyColumnState({ state: columnDefs, applyOrder: true });
			this.gridApi.sizeColumnsToFit();
		}

		if (this.displayTotal) {
			const aggregateFilter = this.contextDatacenter.createAggregateCustomViewFilter(view);
			const aggregateFields = this.contextDatacenter.getAggregateFields();
			this.isFilterContainsAggregateField = aggregateFields.some(value => aggregateFilter.includes(value));
			if (!this.isFilterContainsAggregateField) {
				this.gridApi?.setGridOption('pinnedTopRowData', [{}]);
				this.aggregateSubscription = this.contextDatacenter.setTotal(this.gridApi, aggregateFilter);
			}
			this.dataGridReady.emit(this.gridApi);
		}
	}

	filtersChanged() {
		if (!this.gridApi) {
			this.numberOfFiltersApplied = 0;
			return;
		}
		let numberOfFiltersApplied = 0;

		// Filters in header
		for (const column of this.gridApi.getAllDisplayedColumns()) {
			if (this.gridApi.getColumnFilterModel(column.getColId())) {
				numberOfFiltersApplied++;
			}
		}
		this.numberOfFiltersApplied = numberOfFiltersApplied;
	}

	resetFiltersInHeader() {
		if (!this.gridApi) {
			return;
		}
		this.gridApi.setFilterModel(null);
	}

	getDownloadZipURL(exportDatas: Document[]) {
		const documentIds: string[] = [];
		for (const exportData of exportDatas) {
			documentIds.push(exportData.id.toString());
		}
		if (documentIds.length > 0) {
			open(this.getDownloadZipUrl(documentIds, 'DocumentsZip'));
		}
	}

	onFilterChanged() {
		let filterModel = this.gridApi?.getFilterModel();
		if (filterModel && Object.keys(filterModel).length === 0) {
			filterModel = undefined;
		}
		if (this.displayTotal) {
			const aggregateFilter = this.contextDatacenter.createAggregateQuickSearchFilter(
				this.currentSelectedView,
				this.gridApi,
				filterModel
			);
			const aggregateFields = this.contextDatacenter.getAggregateFields();
			this.isFilterContainsAggregateField = aggregateFields.some(value => aggregateFilter.includes(value));
			if (!this.isFilterContainsAggregateField) {
				this.gridApi?.setGridOption('pinnedTopRowData', [{}]);
				this.aggregateSubscription = this.contextDatacenter.setTotal(this.gridApi, aggregateFilter);
			} else {
				this.gridApi?.setGridOption('pinnedTopRowData', []);
			}
		}
	}

	onSortChanged() {
		const columnState = this.gridApi?.getColumnState();
		this.gridApi?.applyColumnState({
			state: columnState
		});
	}

	private getCurrentView() {
		return this.viewService.getCurrentView(this.defaultConfiguration.getDefaultView(this.datacenterType));
	}

	private resolveViewFromUrlParam(viewParam: string | null, defaultView: UserDatacenterView): UserDatacenterView {
		if (!viewParam) {
			return defaultView;
		}

		if (/^\d+$/.test(viewParam.trim())) {
			return this.userViews.find(v => v.id === Number(viewParam)) || defaultView;
		}

		const decodedViewName = this.safeDecodeURIComponent(viewParam);
		const normalizedViewName = this.normalizeViewName(decodedViewName);
		const views = this.userViews.filter(view => this.normalizeViewName(view.name) === normalizedViewName);

		if (views.length === 0) {
			this.snackBar.open(
				this.translationService.getTR('datacenter.view-not-found', { viewName: decodedViewName }),
				undefined,
				{ duration: 3000 }
			);
			return defaultView;
		}

		if (views.length === 1) {
			return views[0];
		}

		return [...views].sort(
			(a, b) => this.getViewTimestamp(b) - this.getViewTimestamp(a)
		)[0];
	}

	private normalizeViewName(viewName: string): string {
		return viewName.trim().toLowerCase().replace(/\s+/g, '-');
	}

	private getViewTimestamp(view: UserDatacenterView): number {
		const dateValue: unknown = view.modificationDate ?? view.creationDate;
		if (
			dateValue === null
			|| dateValue === undefined
			|| (
				typeof dateValue !== 'string'
				&& typeof dateValue !== 'number'
				&& !(dateValue instanceof Date)
			)
		) {
			return 0;
		}

		const timestamp = new Date(dateValue).getTime();
		return Number.isNaN(timestamp) ? 0 : timestamp;
	}

	private safeDecodeURIComponent(value: string): string {
		try {
			return decodeURIComponent(value);
		} catch (error) {
			console.warn('Unable to decode view parameter', error);
			return value;
		}
	}

	private getGridOptions(): GridOptions {
		const appliedView = this.getCurrentView();
		const { gridState } = this;
		const defaultGridOptions = new CommonGridOptions({
			alignedGrids: [],
			translationService: this.translationService,
			onFilterChanged: this.onFilterChanged.bind(this),
			rowModelType: 'serverSide',
			multiSortKey: 'ctrl',
			cacheBlockSize: LIMIT,
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			alwaysShowVerticalScroll: true,
			columnDefs: this.createColumnDefs(appliedView),
			icons: {
				filter: filterIcon,
			},
			serverSideInitialRowCount: gridState ? (Math.floor((gridState.gridIndex + 1) / 250) + 2) * 250 : 1,
			headerHeight: 70,
			onGridPreDestroyed: () => {
				if (this.gridApi) {
					const filterModel = this.gridApi.getFilterModel();
					CustomLocation.SET_STATE(
						'filterState',
						this.stateId,
						filterModel
					);

					const sortState = this.gridApi.getColumnState();
					CustomLocation.SET_STATE(
						'sortState',
						this.stateId,
						sortState
					);

					CustomLocation.SET_STATE(
						'gridState',
						this.stateId,
						{
							gridIndex: this.gridApi
								// eslint-disable-next-line @typescript-eslint/no-deprecated
								.getModel()
								.getRowIndexAtPixel(this.gridApi.getVerticalPixelRange().top)
						}
					);
				}
			}
		});
		return this.contextDatacenter.getCustomGridOptions(defaultGridOptions);
	}

	private createColumnDefs(appliedView: UserDatacenterView) {
		let allColumns = <ColDefWithFields[]> this.defaultConfiguration.getAllColumnsOfDatacenter(appliedView.type);
		const groupIds = [
			...new Set(allColumns.filter(item => item.groupId !== undefined)
				.map(item => item.groupId ?? ''))
		];
		if (groupIds.length > 0) {
			allColumns = allColumns.filter(allcol => groupIds.length > 0
				&& !groupIds.includes(allcol.colId ?? ''));
		}

		const cols = this.viewService.convertViewToColumnDefinitions(appliedView, allColumns);
		// handle checkbox on first column
		const firstCol = safe(cols.find(col => !col.hide && !col.isMandatoryOnLeft));
		const allPinned = cols.filter(col => !col.hide).every(col => col.pinned);
		return cols.map(colDef => {
			const isFirstCol = firstCol.colId === colDef.colId;
			const extraWidth = isFirstCol ? 24 : 0;
			return {
				...colDef,
				width: colDef.width !== undefined ? colDef.width + extraWidth : undefined,
				minWidth: (colDef.minWidth ?? 0) + extraWidth,
				maxWidth: colDef.maxWidth !== undefined ? colDef.maxWidth + extraWidth : undefined,
				checkboxSelection: isFirstCol,
				headerComponent: CheckboxHeaderComponent,
				headerComponentParams: { showCheckbox: isFirstCol, onDataGridReady: this.dataGridReady },
				pinned: allPinned ? false : colDef.pinned ?? false,
				cellRendererSelector: (params: ICellRendererParams<unknown>) =>
					params.node.rowPinned && (!params.value || isFirstCol)
						? isFirstCol
							? {
								component: () => `<span class="total-overlay">${
									this.translationService.getTR('datacenter.total')
								}</span>`,
								params: undefined
							}
							: {
								component: undefined,
								params: undefined
							}
						: {
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							component: colDef.cellRenderer,
							// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
							params: colDef.cellRendererParams
						}
			};
		});
	}

	private getSupplierOrderTypeValue(supplierOrderType: string): string {
		const translations: Record<string, string> = {
			C: this.translationService.getTR('filter.spot-order'),
			E: this.translationService.getTR('filter.schedule-business-order'),
			F: this.translationService.getTR('filter.schedule-business-order'),
		};

		if (supplierOrderType) {
			const typesArray = supplierOrderType.split(';');
			const translatedValues = typesArray.map(type => translations[type] || type);
			return translatedValues.join(';');
		}
		return supplierOrderType;
	}

	private buildShipmentDuplicateData(dataToExport: Partial<Shipment>[]) {
		let exportData = dataToExport;
		for (const data of dataToExport) {
			if (data.batchReferenceConcat?.includes(';')) {
				const batchReferenceConcat = data.batchReferenceConcat.split(';');
				const customerArticleReferenceConcat = data.customerArticleReferenceConcat?.includes(';')
					? data.customerArticleReferenceConcat.split(';') : [];
				const customerOrderAndItemConcat = data.customerOrderAndItemConcat?.includes(';')
					? data.customerOrderAndItemConcat.split(';') : [];
				const qtyBatchBrutConcat = data.qtyBatchBrutConcat?.includes(';')
					? data.qtyBatchBrutConcat.split(';') : [];
				const steelgradeDescriptionConcat = data.steelgradeDescriptionConcat?.includes(';')
					? data.steelgradeDescriptionConcat.split(';') : [];
				const supplierBatchReferenceConcat = data.supplierBatchReferenceConcat?.includes(';')
					? data.supplierBatchReferenceConcat.split(';') : [];
				const supplierOrderAndItemConcat = data.supplierOrderAndItemConcat?.includes(';')
					? data.supplierOrderAndItemConcat.split(';') : [];
				const thicknessDimensionConcat = data.thicknessDimensionConcat?.includes(';')
					? data.thicknessDimensionConcat.split(';') : [];
				const transportReferenceConcat = data.transportReferenceConcat?.includes(';')
					? data.transportReferenceConcat.split(';') : [];
				const widthDimensionConcat = data.widthDimensionConcat?.includes(';')
					? data.widthDimensionConcat.split(';') : [];
				const lengthDimensionConcat = data.lengthDimensionConcat?.includes(';')
					? data.lengthDimensionConcat.split(';') : [];
				const producedLengthDimensionConcat = data.producedLengthDimensionConcat?.includes(';')
					? data.producedLengthDimensionConcat.split(';') : [];
				const readyToShipDateConcat = data.readyToShipDateConcat?.includes(';')
					? data.readyToShipDateConcat.split(';') : [];
				const transportMode = data.transportMode?.includes(';')
					? data.transportMode.split(';') : [];
				const transportModeDescription = data.transportModeDescription?.includes(';')
					? data.transportModeDescription.split(';') : [];
				const deliveryModeConcat = data.deliveryModeConcat?.includes(';')
					? data.deliveryModeConcat.split(';') : [];
				const deliveryModeDescriptionConcat = data.deliveryModeDescriptionConcat?.includes(';')
					? data.deliveryModeDescriptionConcat.split(';') : [];
				const productCategory = data.productCategory?.includes(';')
					? data.productCategory.split(';') : [];
				const productCategoryDescription = data.productCategoryDescription?.includes(';')
					? data.productCategoryDescription.split(';') : [];
				const serviceLevelConcat = data.serviceLevelConcat?.includes(';')
					? data.serviceLevelConcat.split(';') : [];
				const serviceLevelDescriptionConcat = data.serviceLevelDescriptionConcat?.includes(';')
					? data.serviceLevelDescriptionConcat.split(';') : [];
				const supplierOrderTypeConcat = data.supplierOrderTypeConcat?.includes(';')
					? this.getSupplierOrderTypeValue(data.supplierOrderTypeConcat).split(';') : [];
				const cddWeek = data.cddWeek?.includes(';')
					? data.cddWeek.split(';') : [];
				for (const [index, reference] of batchReferenceConcat.entries()) {
					const duplicateData = { ...data };
					duplicateData.batchReferenceConcat = reference;
					duplicateData.customerArticleReferenceConcat = customerArticleReferenceConcat[index];
					duplicateData.customerOrderAndItemConcat = customerOrderAndItemConcat[index];
					duplicateData.qtyBatchBrutConcat = qtyBatchBrutConcat[index]
						? Number.parseFloat(qtyBatchBrutConcat[index]).toString()
						: '';
					duplicateData.steelgradeDescriptionConcat = steelgradeDescriptionConcat[index];
					duplicateData.supplierBatchReferenceConcat = supplierBatchReferenceConcat[index];
					duplicateData.supplierOrderAndItemConcat = supplierOrderAndItemConcat[index];
					duplicateData.thicknessDimensionConcat = thicknessDimensionConcat[index]
						? Number.parseFloat(thicknessDimensionConcat[index]).toString()
						: '';
					duplicateData.transportReferenceConcat = transportReferenceConcat[index];
					duplicateData.widthDimensionConcat = widthDimensionConcat[index]
						? Number.parseFloat(widthDimensionConcat[index]).toString()
						: '';
					duplicateData.lengthDimensionConcat = lengthDimensionConcat[index] === '0.000000'
						? ''
						: lengthDimensionConcat[index] ?? duplicateData.lengthDimensionConcat;
					duplicateData.producedLengthDimensionConcat = producedLengthDimensionConcat[index]
						?? duplicateData.producedLengthDimensionConcat;
					duplicateData.readyToShipDateConcat = readyToShipDateConcat[index]
						?? duplicateData.readyToShipDateConcat;
					duplicateData.transportMode = transportMode[index] ?? duplicateData.transportMode;
					duplicateData.transportModeDescription = transportModeDescription[index]
						?? duplicateData.transportModeDescription;
					duplicateData.deliveryModeConcat = deliveryModeConcat[index] ?? duplicateData.deliveryModeConcat;
					duplicateData.deliveryModeDescriptionConcat = deliveryModeDescriptionConcat[index]
						?? duplicateData.deliveryModeDescriptionConcat;
					duplicateData.productCategory = productCategory[index] ?? duplicateData.productCategory;
					duplicateData.productCategoryDescription = productCategoryDescription[index]
						?? duplicateData.productCategoryDescription;
					duplicateData.serviceLevelConcat = serviceLevelConcat[index] ?? duplicateData.serviceLevelConcat;
					duplicateData.serviceLevelDescriptionConcat = serviceLevelDescriptionConcat[index]
						?? duplicateData.serviceLevelDescriptionConcat;
					duplicateData.supplierOrderTypeConcat = supplierOrderTypeConcat[index];
					duplicateData.cddWeek = cddWeek[index];
					exportData.push(duplicateData);
				}
			}
		}
		exportData = exportData.filter(data => {
			data.customerOrderAndItemConcat = data.customerOrderAndItemConcat
				? resolveSingleConcatenatedData(data.customerOrderAndItemConcat) : '';
			data.supplierOrderAndItemConcat = data.supplierOrderAndItemConcat
				? resolveSingleConcatenatedData(data.supplierOrderAndItemConcat) : '';
			data.qtyBatchBrutConcat = data.qtyBatchBrutConcat
				? Number.parseFloat(data.qtyBatchBrutConcat).toString() : '';
			data.thicknessDimensionConcat = data.thicknessDimensionConcat
				? Number.parseFloat(data.thicknessDimensionConcat).toString() : '';
			data.widthDimensionConcat = data.widthDimensionConcat
				? Number.parseFloat(data.widthDimensionConcat).toString() : '';
			data.lengthDimensionConcat = data.lengthDimensionConcat
				? data.lengthDimensionConcat === '0.000000' ? '' : data.lengthDimensionConcat
				: '';
			data.supplierOrderTypeConcat = data.supplierOrderTypeConcat
				? this.getSupplierOrderTypeValue(data.supplierOrderTypeConcat) : '';
			return !data.batchReferenceConcat?.includes(';');
		});
		return exportData;
	}

	private applyFieldsToGrid(fields: string): void {
		if (!this.gridApi || !this.dataSource) {
			return;
		}

		const view: UserDatacenterView = {
			...this.currentSelectedView,
			columnState: fields.split(',').map(colId => ({ colId }))
		};

		this.currentSelectedView = view;
		this.updateCurrentColumns(view);
		this.dataSource.currentColumns = this.currentColumns;
		this.dataSource.currentView = view;

		const columnDefs = this.createColumnDefs(view);

		this.gridApi.setGridOption('columnDefs', columnDefs);
		this.gridApi.applyColumnState({ state: <ColumnState[]>view.columnState, applyOrder: true });
		this.gridApi.refreshServerSide({ purge: true });
		this.gridApi.sizeColumnsToFit();
	}
}
